import React, { useState, useMemo, useCallback, useEffect } from 'react';

import {
  Breakpoints,
  CTA,
  CustomSlider,
  Tabs,
  TextAppearance,
  TextCustom,
  TextLineHeight,
  TextSize,
  TextTag,
  TextWeight,
} from '@vw-marketing/us-components';

import { AppConstants } from '../../utils/app-constants';
import {
  addLastMaintenanceFlag,
  filterPackagesByDescription,
  formatIntervalValue,
  getMaintenanceItemsByInterval,
  groupAndFormatPackageNotes,
} from '../../utils/maintenance-schedule-utils';
import { InformationCard } from '../../components/InformationCard/InformationCard';
import { Layer } from '../../components/Layer/Layer';
import {
  MaintenanceLineItem,
  MaintenanceSchedule,
  TimeBasedServices,
} from '../../typings/general';
import { MaintenanceTitle } from '../../components/MaintenaceTitle/MaintenanceTitle';
import { ModelCard } from '../../components/ModelCard/ModelCard';
import { NextServiceCard } from '../../components/NextServiceCard/NextServiceCard';
import { parseMileageNumber } from '../../utils/general';
import { ServicesLayer } from '../../components/InformationCard/ServicesLayer/ServicesLayer';
import {
  InfoCardsContainer,
  StyledCTAContainer,
  StyledCTAs,
  StyledMaintenanceLeftContainer,
  StyledMaintenanceRightContainer,
  StyledMaintenanceWrapper,
  StyledTabsContainer,
  StyledWrapper,
  SytledMaintenanceContainer,
} from './styled';
import {
  onVWBasicButtonClickEvent,
  onVWBasicLayerClose,
  onVWBasicLinkClickEvent,
} from '../../tagging/tagging-helpers';
import { useFeatureAppConfig } from '../../hooks/use-feature-app-config';
import { useMaintenanceContext } from '../../hooks/use-maintenance-context';
import { useStore } from '../../hooks-store/store';
import { useTrackingManager } from '../../hooks/use-tracking-manager';
import { useViewport, ViewportType } from '../../hooks/use-viewport';

const byDescription = (
  a: MaintenanceLineItem | TimeBasedServices,
  b: MaintenanceLineItem | TimeBasedServices,
) => {
  const ad = (a.description ?? '').trim();
  const bd = (b.description ?? '').trim();
  return ad.localeCompare(bd, undefined, {
    sensitivity: 'base',
    numeric: true,
  });
};

export const Maintenance = ({
  maintenanceSchedule,
  setMaintenanceSchedule
}: {
  maintenanceSchedule: MaintenanceSchedule;
  setMaintenanceSchedule: (value: any) => void;
}) => {
  const [openCompleteView, setOpenCompleteView] = useState<boolean>(true);
  const [openContentLayer, setOpenContentLayer] = useState<boolean>(false);
  const [showTabsWithMileage, setShowTabsWithMileage] =
    useState<boolean>(false);
  const [openContentLayerIndex, setOpenContentLayerIndex] = useState<number>(0);
  const [openContentLayerInterval, setOpenContentLayerInterval] = useState<
    string | null
  >(null);

  const [store] = useStore();
  const { mode } = useFeatureAppConfig();
  const { translationHandler } = useMaintenanceContext();
  const trackingManager = useTrackingManager();

  const { currentData, form } = store;
  const { carImage } = currentData;
  const { mileage } = form;

  const formattedMileage: number = parseMileageNumber(mileage) ?? 0;
  const isAtMilestone = formattedMileage % 10000 === 0 && formattedMileage > 0;

  const viewport: ViewportType = useViewport(
    Breakpoints.b560,
    Breakpoints.b1280,
  );
  const viewport2: ViewportType = useViewport(
    Breakpoints.b560,
    Breakpoints.b1600,
  );
  const isDesktop: boolean = viewport === ViewportType.DESKTOP;
  const isDesktopB1600: boolean = viewport2 === ViewportType.DESKTOP;
  const isMileageInputted: boolean = !!mileage;
  const viewScheduleCTALabel: string = openCompleteView
    ? translationHandler('maintenance.complete.schedule')
    : translationHandler('maintenance.summerized.schedule');

  const isVehicleEV = useMemo(
    () => ['ID4', 'ID. BUZZ'].includes(currentData.model?.toUpperCase() ?? ''),
    [currentData.model],
  );

  const maintenanceItemsFilteredByMileage = useMemo(
    () =>
      getMaintenanceItemsByInterval(
        maintenanceSchedule.packages,
        formattedMileage,
      ),
    [maintenanceSchedule.packages, formattedMileage],
  );

  const defaultServiceItem = useMemo(
    () =>
      formattedMileage <= 10000 ||
        Object.values(maintenanceItemsFilteredByMileage).length <= 1
        ? 0
        : 1,
    [formattedMileage],
  );

  const getServiceLabel = useCallback(
    (index: number, cardsAmount: number) => {
      const LABELS = {
        FIRST_SERVICE: 'maintenance.schedule.first.service.label',
        PREVIOUS_SERVICE: 'maintenance.schedule.previous.service.label',
        CURRENT_SERVICE: 'maintenance.current.service',
        NEXT_SERVICE: 'maintenance.schedule.next.service.label',
        FUTURE_SERVICE: 'maintenance.future.service',
      };

      const translate = (key: string) => translationHandler(key);

      // Case 1: Low mileage vehicles (under 10,000 miles)
      if (formattedMileage <= AppConstants.FIRST_SERVICE_MILEAGE) {
        return index === 0
          ? translate(LABELS.FIRST_SERVICE)
          : translate(LABELS.FUTURE_SERVICE);
      }

      // Case 2: Exactly at last service mileage (360k) with 2 cards
      if (
        formattedMileage === AppConstants.LAST_SERVICE_MILEAGE &&
        cardsAmount === 2
      ) {
        return index === 0
          ? translate(LABELS.PREVIOUS_SERVICE)
          : translate(LABELS.CURRENT_SERVICE);
      }

      // Case 3: Before last service mileage with 2 cards
      if (
        formattedMileage < AppConstants.LAST_SERVICE_MILEAGE &&
        cardsAmount === 2
      ) {
        return index === 0
          ? translate(LABELS.PREVIOUS_SERVICE)
          : translate(LABELS.NEXT_SERVICE);
      }

      // Case 4: At milestone intervals (20K, 30K, etc.)
      if (isAtMilestone) {
        switch (index) {
          case 0:
            return translate(LABELS.PREVIOUS_SERVICE);
          case 1:
            return translate(LABELS.CURRENT_SERVICE);
          default:
            return translate(LABELS.FUTURE_SERVICE);
        }
      }

      // Case 5: Default - all other mileages
      switch (index) {
        case 0:
          return translate(LABELS.PREVIOUS_SERVICE);
        case 1:
          return translate(LABELS.NEXT_SERVICE);
        default:
          return translate(LABELS.FUTURE_SERVICE);
      }
    },
    [formattedMileage, isAtMilestone, translationHandler],
  );

  const getMaintenanceInformation = useCallback(
    (label?: string) => {
      switch (label) {
        case 'Minor Maintenance':
          return {
            label: translationHandler('maintenance.minor'),
            description: translationHandler('maintenance.minor.description'),
            frequency: translationHandler('maintenance.minor.frequency'),
            frequencyDescription: translationHandler(
              'maintenance.minor.frequency.description',
            ),
            whatToExpect: translationHandler('maintenance.minor.expect'),
          };
        case 'Standard Maintenance':
          return {
            label: translationHandler('maintenance.standard'),
            description: translationHandler('maintenance.standard.description'),
            frequency: translationHandler('maintenance.standard.frequency'),
            frequencyDescription: translationHandler(
              'maintenance.standard.frequency.description',
            ),
            whatToExpect: isVehicleEV
              ? translationHandler('maintenance.standard.ev.expect')
              : translationHandler('maintenance.standard.expect'),
          };
        case 'Standard Maintenance - small':
          return {
            label: translationHandler('maintenance.standard.small'),
            description: translationHandler('maintenance.standard.description'),
            frequency: translationHandler('maintenance.standard.frequency'),
            frequencyDescription: translationHandler(
              'maintenance.standard.frequency.description',
            ),
            whatToExpect: translationHandler(
              'maintenance.standard.small.expect',
            ),
          };
        case 'Extended Maintenance':
          return {
            label: translationHandler('maintenance.extended'),
            description: translationHandler('maintenance.extended.description'),
            frequency: translationHandler('maintenance.extended.frequency'),
            frequencyDescription: translationHandler(
              'maintenance.extended.frequency.description',
            ),
            whatToExpect: translationHandler('maintenance.extended.expect'),
          };
        default:
          return {
            description: '',
            frequency: '',
            frequencyDescription: '',
            label: translationHandler('maintenance.default'),
            whatToExpect: '',
          };
      }
    },
    [translationHandler, isVehicleEV],
  );

  const cardsData = useMemo(() => {
    const entries = isMileageInputted
      ? Object.entries(maintenanceItemsFilteredByMileage)
      : Object.entries(addLastMaintenanceFlag(maintenanceSchedule.packages));

    return entries.map(([interval, items]) => {
      const lineItems = items[0].maintenance_line_item;
      const groupedAndFormatted = groupAndFormatPackageNotes(lineItems);
      const info = getMaintenanceInformation(groupedAndFormatted.combinedLabel);
      const isLastMaintenance: boolean | undefined = items[0].isLastMaintenance;

      const mergedAdditionalForLayer = [
        ...maintenanceSchedule.time_based_services,
        ...filterPackagesByDescription(lineItems, false),
      ].sort(byDescription);

      return {
        interval,
        packages: { ...info, ...groupedAndFormatted },
        // only used by ServicesLayer
        additionalServicesMerged: mergedAdditionalForLayer,
        isLastMaintenance,
      };
    });
  }, [
    isMileageInputted,
    maintenanceItemsFilteredByMileage,
    maintenanceSchedule.packages,
    maintenanceSchedule.time_based_services,
    getMaintenanceInformation,
  ]);

  const fullCardsData = useMemo(() => {
    return Object.entries(maintenanceSchedule.packages).map(
      ([interval, items]) => {
        const lineItems = items[0].maintenance_line_item;
        const groupedAndFormatted = groupAndFormatPackageNotes(lineItems);
        const info = getMaintenanceInformation(
          groupedAndFormatted.combinedLabel,
        );
        const isLastMaintenance = items[0].isLastMaintenance;

        const mergedAdditionalForLayer = [
          ...maintenanceSchedule.time_based_services,
          ...filterPackagesByDescription(lineItems, false),
        ].sort(byDescription);

        return {
          interval,
          packages: { ...info, ...groupedAndFormatted },
          additionalServicesMerged: mergedAdditionalForLayer,
          isLastMaintenance,
        };
      },
    );
  }, [maintenanceSchedule.packages, maintenanceSchedule.time_based_services]);

  const openFindADealer = useCallback(() => {
    const localeRegex = /(\/)([a-z]{2})(\/)/i;
    const currentUrl = window.location.href;
    const match = currentUrl.match(localeRegex);
    let findADealerUrl = '';
    const dealerSearch = 'dealer-search.html';

    if (match && match.index) {
      const localeSegment = match[0];
      const baseUrl = currentUrl.substring(
        0,
        match.index + localeSegment.length,
      );
      findADealerUrl = baseUrl + dealerSearch;
    } else {
      findADealerUrl = currentUrl + dealerSearch;
    }
    window.open(findADealerUrl, '_blank', 'noopener,noreferrer');
  }, []);

  const handleFindDealerTracking = () => {
    onVWBasicButtonClickEvent(
      trackingManager,
      {
        Type: 'Button',
        Name: translationHandler('maintenance.find.dealer'),
        ItemCount: 2,
        ItemPosition: 1,
      },
      undefined,
      mode,
    );
  };

  const handleViewCompleteScheduleTracking = () => {
    const link = {
      name: viewScheduleCTALabel,
      url: window.location.href
    }

    onVWBasicLinkClickEvent(
      trackingManager,
      {
        Type: 'Link',
        Name: viewScheduleCTALabel,
        ItemCount: 2,
        ItemPosition: 2,
      },
      'Link',
      undefined, // document data if needed
      undefined, // additional tracking data
      mode,
      link
    );
  };

  const handleCloseLayerTracking = () => {
    const vehicle: any = {
      modelName: store.currentData.model,
      modelYear: store.currentData.modelYear,
      trim: store.currentData.trim,
      vin: store.currentData.vin,
      odometer: parseMileageNumber(store.form.mileage)
    }

    onVWBasicLayerClose(trackingManager, mode, vehicle);
  };

  const renderCTAs = useMemo(
    () => (
      <StyledCTAs {...{ isMileageInputted, openCompleteView }}>
        <CTA
          tag="button"
          emphasis="primary"
          onClick={() => {
            openFindADealer();
            handleFindDealerTracking();
          }}
          aria-label={translationHandler('maintenance.find.dealer')}
        >
          <TextCustom
            appearance={TextAppearance.label200}
            tag={TextTag.p}
            style={{
              [Breakpoints.default]: {
                fontSize: TextSize.TextSize16,
                fontWeight: TextWeight.regular,
                lineHeight: TextLineHeight.TextLineHeight20,
              },
            }}
          >
            {translationHandler('maintenance.find.dealer')}
          </TextCustom>
        </CTA>
        {isMileageInputted && (
          <CTA
            tag="button"
            emphasis={isDesktop ? 'secondary' : 'tertiary'}
            onClick={() => {
              setOpenCompleteView(!openCompleteView);
              isMileageInputted && setShowTabsWithMileage(!showTabsWithMileage);
              handleViewCompleteScheduleTracking();
            }}
            aria-label={viewScheduleCTALabel}
          >
            <TextCustom
              appearance={TextAppearance.label200}
              tag={TextTag.p}
              style={{
                [Breakpoints.default]: {
                  fontSize: TextSize.TextSize16,
                  fontWeight: TextWeight.regular,
                  lineHeight: TextLineHeight.TextLineHeight20,
                },
              }}
              aria-label={viewScheduleCTALabel}
            >
              {viewScheduleCTALabel}
            </TextCustom>
          </CTA>
        )}
      </StyledCTAs>
    ),
    [
      isDesktop,
      openFindADealer,
      translationHandler,
      viewScheduleCTALabel,
      openCompleteView,
      isMileageInputted,
    ],
  );

  const renderInformationCard = (
    interval: string,
    index: number,
    label: string | undefined,
    forceUseFullData: boolean = false,
  ) => {
    // If we're rendering tabs with all data but cardsData is filtered
    if (forceUseFullData && isMileageInputted) {
      // Use the full data directly
      const tempData = fullCardsData.find((item) => item.interval === interval);
      if (!tempData) return null;

      const fullDataIndex = fullCardsData.findIndex(
        (item) => item.interval === interval,
      );

      return (
        <InformationCard
          interval={interval}
          label={label ?? translationHandler('maintenance.selected.mileage')}
          // @ts-ignore
          packages={tempData.packages}
          additionalServices={maintenanceSchedule.time_based_services}
          openCompleteScheduleView={openCompleteView}
          key={interval}
          onOpenContentLayer={(value) => {
            setOpenContentLayer(value);
            setOpenContentLayerIndex(fullDataIndex);
            setOpenContentLayerInterval(interval);
          }}
          isLastMaintenance={tempData.isLastMaintenance}
        />
      );
    }

    const data = cardsData[index];
    if (!data) return null;

    return (
      <InformationCard
        interval={interval}
        label={label ?? translationHandler('maintenance.selected.mileage')}
        // @ts-ignore
        packages={data.packages}
        additionalServices={maintenanceSchedule.time_based_services}
        openCompleteScheduleView={openCompleteView}
        key={interval}
        onOpenContentLayer={(value) => {
          setOpenContentLayer(value);
          setOpenContentLayerIndex(index);
          setOpenContentLayerInterval(interval);
        }}
        isLastMaintenance={data.isLastMaintenance}
      />
    );
  };

  const renderSliderContent = useMemo(
    () =>
      Object.entries(maintenanceItemsFilteredByMileage).map(
        ([interval, _items], index) =>
          renderInformationCard(
            interval,
            index,
            getServiceLabel(
              index,
              Object.entries(maintenanceItemsFilteredByMileage).length,
            ),
          ),
      ),
    [maintenanceItemsFilteredByMileage, renderInformationCard, getServiceLabel],
  );

  /**
   * Disable scroll in the main content when the Layer is open
   */
  const lockScroll = useCallback(() => {
    const scrollY = window.scrollY;

    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';

    return () => {
      // When effect cleanup runs, restore scroll position
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      window.scrollTo(0, scrollY);
    };
  }, []);

  useEffect(() => {
    if (openContentLayer) {
      const unlockScroll = lockScroll();
      return () => unlockScroll();
    }

    return () => { };
  }, [openContentLayer, lockScroll]);

  const renderTabsContent = useMemo(
    () =>
      Object.entries(maintenanceSchedule.packages).map(
        ([interval, _items], index) => ({
          title: formatIntervalValue(Number(interval)),
          content: (
            <StyledWrapper isCardWithinATab>
              {renderInformationCard(interval, index, undefined, true)}

              {openContentLayer && (
                <Layer
                  active={openContentLayer}
                  onClick={() => {
                    handleCloseLayerTracking();
                    setOpenContentLayer(false);
                  }}
                  onClose={() => {
                    setOpenContentLayer(false);
                    handleCloseLayerTracking();
                  }}
                  closeButtonLabel="Close Layer"
                >
                  <ServicesLayer
                    // @ts-ignore
                    packages={
                      openContentLayerInterval
                        ? fullCardsData.find(
                          (item) =>
                            item.interval === openContentLayerInterval,
                        )?.packages
                        : cardsData[openContentLayerIndex]?.packages
                    }
                    // @ts-ignore
                    additionalServices={
                      openContentLayerInterval
                        ? fullCardsData.find(
                          (item) =>
                            item.interval === openContentLayerInterval,
                        )?.additionalServicesMerged
                        : cardsData[openContentLayerIndex]
                          ?.additionalServicesMerged
                    }
                    showAllServices
                    openContentLayer={openContentLayer}
                  />
                </Layer>
              )}
            </StyledWrapper>
          ),
          key: interval,
        }),
      ),
    [maintenanceSchedule.packages, renderCTAs, renderInformationCard],
  );

  const getDefaultTabIndex = useMemo(() => {
    if (!isMileageInputted) return 0;

    const intervals = Object.keys(maintenanceSchedule.packages).map(Number);

    const exactMatchIndex = intervals.findIndex(
      (interval) => interval === formattedMileage,
    );

    if (exactMatchIndex >= 0) return exactMatchIndex;

    const nextInterval = intervals.find(
      (interval) => interval > formattedMileage,
    );

    if (nextInterval) return intervals.indexOf(nextInterval);

    // If no higher value exists, return the last interval
    return intervals.length - 1;
  }, [isMileageInputted, maintenanceSchedule.packages]);

  return (
    <SytledMaintenanceContainer>
      <ModelCard setMaintenanceSchedule={setMaintenanceSchedule} />
      <StyledMaintenanceWrapper
        carImage={carImage}
        showTabsWithMileage={showTabsWithMileage}
        isMileageInputted={isMileageInputted}
      >
        <StyledMaintenanceLeftContainer
          showTabsWithMileage={showTabsWithMileage}
          cardsAmount={renderSliderContent.length}
          isMileageInputted={isMileageInputted}
        >
          <MaintenanceTitle />
          {isMileageInputted ? (
            <>
              <NextServiceCard
                setMaintenanceSchedule={setMaintenanceSchedule}
                cardsAmount={renderSliderContent.length}
                maintenanceScheduleData={maintenanceSchedule}
              />
              {!openCompleteView && isDesktopB1600 && renderCTAs}
            </>
          ) : (
            <StyledCTAContainer>
              <CTA
                tag="button"
                emphasis="tertiary"
                aria-label="Add Mileage CTA"
                onClick={() => {
                  setMaintenanceSchedule && setMaintenanceSchedule(null);
                }}
                stretchContent
              >
                <TextCustom
                  appearance={TextAppearance.copy200}
                  tag={TextTag.p}
                  style={{
                    [Breakpoints.default]: {
                      fontSize: TextSize.TextSize14,
                      fontWeight: TextWeight.light,
                      lineHeight: TextLineHeight.TextLineHeight24,
                    },
                    [Breakpoints.b1600]: {
                      fontSize: TextSize.TextSize16,
                      fontWeight: TextWeight.light,
                      lineHeight: TextLineHeight.TextLineHeight24,
                    },
                  }}
                >
                  {translationHandler('maintenance.schedule.add.mileage.label')}
                </TextCustom>
              </CTA>
              {isDesktopB1600 && renderCTAs}
            </StyledCTAContainer>
          )}
        </StyledMaintenanceLeftContainer>
        <StyledMaintenanceRightContainer>
          {isMileageInputted && !showTabsWithMileage && (
            <StyledWrapper>
              {!isDesktop ? (
                <CustomSlider
                  buttonsPosition="top"
                  initialSlideToShow={defaultServiceItem}
                  onSlideChange={() => setOpenCompleteView(true)}
                  scrollByOne
                  slides={renderSliderContent}
                  slidesToShow={3}
                  slidesToShowMedium={3}
                  slidesToShowMobile={1.1}
                  slidesToShowTablet={1.1}
                />
              ) : (
                <InfoCardsContainer length={renderSliderContent.length}>
                  {renderSliderContent}
                </InfoCardsContainer>
              )}

              {openContentLayer && (
                <Layer
                  active={openContentLayer}
                  closeButtonLabel="Close Layer"
                  onClick={() => {
                    handleCloseLayerTracking();
                    setOpenContentLayer(false);
                  }}
                  onClose={() => {
                    setOpenContentLayer(false);
                    handleCloseLayerTracking();
                  }}
                >
                  <ServicesLayer
                    // @ts-ignore
                    packages={
                      openContentLayerInterval
                        ? fullCardsData.find(
                          (item) =>
                            item.interval === openContentLayerInterval,
                        )?.packages
                        : cardsData[openContentLayerIndex]?.packages
                    }
                    // @ts-ignore
                    additionalServices={
                      openContentLayerInterval
                        ? fullCardsData.find(
                          (item) =>
                            item.interval === openContentLayerInterval,
                        )?.additionalServicesMerged
                        : cardsData[openContentLayerIndex]
                          ?.additionalServicesMerged
                    }
                    showAllServices
                    openContentLayer={openContentLayer}
                  />
                </Layer>
              )}
              {(openCompleteView || !isDesktopB1600) && renderCTAs}
            </StyledWrapper>
          )}
          {((isMileageInputted && showTabsWithMileage) ||
            !isMileageInputted) && (
              <StyledTabsContainer
                cardsLength={renderSliderContent.length}
                showTabsWithMileage={showTabsWithMileage}
              >
                <Tabs
                  variant="step navigation"
                  idPrefix="maintenance-schedule"
                  scrollable
                  defaultIndex={getDefaultTabIndex}
                >
                  {renderTabsContent}
                </Tabs>
                {!isDesktopB1600 && renderCTAs}
              </StyledTabsContainer>
            )}
        </StyledMaintenanceRightContainer>
      </StyledMaintenanceWrapper>
    </SytledMaintenanceContainer>
  );
};
